import { useEffect, useState } from 'react'

/**
 * Tracks whether the viewport matches the "mobile" breakpoint (<768px,
 * same threshold Tailwind uses for `md:`). Used by routing/layout code
 * that can't simply rely on CSS display toggles — e.g. choosing which
 * component to mount for a given route, where rendering both and hiding
 * one would double-trigger effects and fetches.
 *
 * SSR-safe: the initial value reads `window.innerWidth` inside the
 * useState initialiser, so components never mount with the wrong value.
 */
export function useIsMobile(breakpointPx = 768): boolean {
  const [isMobile, setIsMobile] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false
    return window.innerWidth < breakpointPx
  })

  useEffect(() => {
    if (typeof window === 'undefined') return
    const mq = window.matchMedia(`(max-width: ${breakpointPx - 1}px)`)
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches)
    // Modern browsers use addEventListener; Safari <14 uses addListener
    if (mq.addEventListener) mq.addEventListener('change', handler)
    else mq.addListener(handler)
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', handler)
      else mq.removeListener(handler)
    }
  }, [breakpointPx])

  return isMobile
}
